import { inject, Injectable, signal } from '@angular/core';
import { Router, UrlCreationOptions } from '@angular/router';
import { App } from '@capacitor/app';
import { AppUpdateInfo, CONFIG_TOKEN, Patient } from '@pxl/models/common';
import { CommonService, FormatService, PlatformService, TranslatableModel } from '@pxl/models/contracts';
import { AppRoutingRouteNames } from '@pxl/route-names';
import { CacheResponse } from '@pxl/shared/decorators';
import {
    BloodGroupModel,
    BreedModel,
    BreedTypeModel,
    CoatModel,
    CommonService as CommonBackendService,
    CommonReferenceDataModel,
    CountryModel,
    CurrencyModel,
    GenderModel,
    IntoleranceOrAllergyModel,
    LocalizationModel,
    MedicationRouteOfAdministrationModel,
    MedicationUnitModel,
    PasswordRulesResultModel,
    PreExistingConditionModel,
    SalutationModel,
    SpeciesModel,
    SurgeryModel,
    VersionService as VersionBackendService,
    VersionModel,
} from '@pxl/shared/generated';
import { LanguageService } from '@pxl/shared/translation';
import { getSkipAppNeedsUpdateStorage, promise_timeout, reloadOutdatedBrowserApp, SortOrder } from '@pxl/shared/util';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class DefaultCommonService implements CommonService {
    private readonly commonBackendService = inject(CommonBackendService);
    private readonly languageService = inject(LanguageService);
    private readonly formatService = inject(FormatService);
    private readonly versionBackendService = inject(VersionBackendService);
    private readonly platformService = inject(PlatformService);
    private readonly config = inject(CONFIG_TOKEN);
    private readonly router = inject(Router);

    @CacheResponse()
    public onBreeds(): Observable<BreedModel[]> {
        return this.commonBackendService.getBreeds(this.config.API_VERSION);
    }

    @CacheResponse()
    public onSalutations(): Observable<SalutationModel[]> {
        return this.commonBackendService.getSalutation(this.config.API_VERSION);
    }

    @CacheResponse()
    public onCultures(): Observable<LocalizationModel[]> {
        return this.commonBackendService.getLocalizations(this.config.API_VERSION);
    }

    @CacheResponse()
    public onCountries(): Observable<CountryModel[]> {
        return this.commonBackendService.getCountries(this.config.API_VERSION);
    }

    @CacheResponse()
    public onSpecies(): Observable<SpeciesModel[]> {
        return this.commonBackendService.getSpecies(this.config.API_VERSION);
    }

    @CacheResponse()
    public onMedicationRoutesOfAdministration(): Observable<MedicationRouteOfAdministrationModel[]> {
        return this.commonBackendService.getMedicationRoutesOfAdministration(this.config.API_VERSION);
    }

    @CacheResponse()
    public onMedicationUnits(): Observable<MedicationUnitModel[]> {
        return this.commonBackendService.getMedicationUnits(this.config.API_VERSION);
    }

    @CacheResponse()
    public onBreedTypes(): Observable<BreedTypeModel[]> {
        return this.commonBackendService
            .getBreedTypes(this.config.API_VERSION)
            .pipe(map((breedTypes: BreedTypeModel[]) => breedTypes.sort(SortOrder.bySortOrder)));
    }

    @CacheResponse()
    public onCoats(): Observable<CoatModel[]> {
        return this.commonBackendService.getCoats(this.config.API_VERSION);
    }

    @CacheResponse()
    public onGenders(): Observable<GenderModel[]> {
        return this.commonBackendService.getGenders(this.config.API_VERSION);
    }

    @CacheResponse()
    public onCommonReferenceData(): Observable<CommonReferenceDataModel> {
        return this.commonBackendService.getCommonReferenceData(this.config.API_VERSION);
    }

    public onPasswordRules(): Observable<PasswordRulesResultModel> {
        return this.getCachedPasswordRules();
    }

    public getFormattedShortDate(date: Date): string | null {
        const dateOfBirth = date || null;
        return (!!dateOfBirth && this.formatService.getFormattedShortDate(dateOfBirth)) || null;
    }

    public onCommonModelTexts(
        patient: Patient,
    ): Observable<[string | undefined, string | undefined, string | undefined, string | undefined, string | undefined]> {
        const { speciesId, breedId, breedText, speciesText, breedTypeId } = patient;
        const { coatId, coatText, genderId } = patient.animalSpecificData ?? {
            coatId: undefined,
            coatText: undefined,
            genderId: undefined,
        };
        return combineLatest([this.onSpecies(), this.onBreeds(), this.onBreedTypes(), this.onCoats(), this.onGenders()]).pipe(
            map(([species, breeds, breedTypes, coats, genders]) => {
                const modelsIdsPairs: [TranslatableModel[], string | undefined | null, string | undefined | null][] = [
                    [species, speciesId, speciesText],
                    [breeds, breedId, breedText],
                    [breedTypes, breedTypeId, undefined],
                    [coats, coatId, coatText],
                    [genders, genderId, undefined],
                ];
                return modelsIdsPairs.map<string | undefined>(([models, modelId, modelText]) => {
                    const model = models.find((model) => model.id === modelId);
                    return (model ? this.languageService.translate(model.resourceKey) : modelText) ?? undefined;
                }) as [string | undefined, string | undefined, string | undefined, string | undefined, string | undefined];
            }),
        );
    }

    @CacheResponse()
    public onBloodGroups(): Observable<BloodGroupModel[]> {
        return this.commonBackendService.getBloodGroups(this.config.API_VERSION);
    }

    public onFavoriteBloodGroupsForSpecies(speciesId: string): Observable<BloodGroupModel[]> {
        return this.commonBackendService.getFavoriteBloodGroups(speciesId, this.config.API_VERSION);
    }

    @CacheResponse()
    public onIntolerancesOrAllergies(): Observable<IntoleranceOrAllergyModel[]> {
        return this.commonBackendService.getIntolerancesOrAllergies(this.config.API_VERSION);
    }

    @CacheResponse()
    public onPreExistingConditions(): Observable<PreExistingConditionModel[]> {
        return this.commonBackendService.getPreExistingConditions(this.config.API_VERSION);
    }

    @CacheResponse()
    public onSurgeries(): Observable<SurgeryModel[]> {
        return this.commonBackendService.getSurgeries(this.config.API_VERSION);
    }

    public checkWhetherApiIsAvailableAndTimeoutAfter(seconds: number): Promise<boolean> {
        return promise_timeout(seconds * 1000, this.apiAvailabilityCheck())
            .then(() => true)
            .catch(() => false);
    }

    public async appUpdateInfo(): Promise<AppUpdateInfo> {
        const info = await this.apiVersionInfo();
        const currentVersion = Number.parseInt(this.config.API_VERSION, 10);

        const isActive = info.activeVersions.includes(currentVersion);
        const higherVersionExists = info.activeVersions.some((a) => a > currentVersion);
        const currentVersionDeprecated = info.deprecatedVersions.includes(currentVersion);

        const debugUpdate = localStorage.getItem('debug_update');
        if (debugUpdate) {
            switch (debugUpdate) {
                case 'must':
                    return new AppUpdateInfo(true, false, false);
                case 'should':
                    return new AppUpdateInfo(false, true, false);
                case 'can':
                    return new AppUpdateInfo(false, false, true);
            }
        }

        if (!isActive && !currentVersionDeprecated) return new AppUpdateInfo(true, false, false);

        if (currentVersionDeprecated) return new AppUpdateInfo(false, true, false);

        if (higherVersionExists) return new AppUpdateInfo(false, false, true);

        return new AppUpdateInfo(false, false, false);
    }

    @CacheResponse()
    private getCachedPasswordRules(): Observable<PasswordRulesResultModel> {
        return this.commonBackendService.getPasswordRules(this.config.API_VERSION);
    }

    private apiAvailabilityCheck(): Promise<void> {
        return firstValueFrom(this.versionBackendService.availabilityCheck(this.config.API_VERSION));
    }

    public $disabledFeatures = signal<string[]>([]);
    private readonly updateChecker = App.addListener('resume', async () => {
        if (this.platformService.isNativePlatform()) {
            await this.appUpdateCheck();
        }
    });

    public async appUpdateCheck() {
        const updateInfo = await this.appUpdateInfo();

        // The update info page is native-only. In the browser an unsupported version means a stale
        // build is running, so re-fetch index.html instead of offering an update the user cannot do.
        // A merely recommended update is not stale enough to reload for — a 430 then means the
        // feature is off for this practice, not that the build is outdated.
        if (!this.platformService.isNativePlatform()) {
            if (updateInfo.MustUpdate) {
                reloadOutdatedBrowserApp();
            }

            return false;
        }

        const skipIfPossible = getSkipAppNeedsUpdateStorage();
        if ((updateInfo.anyUpdateAvailable() && !skipIfPossible) || updateInfo.MustUpdate) {
            const navigationExtras: UrlCreationOptions = {
                queryParams: {
                    destinationUrl: this.router.url,
                    canUpdate: updateInfo.CanUpdate,
                    mustUpdate: updateInfo.MustUpdate,
                    shouldUpdate: updateInfo.ShouldUpdate,
                },
            };
            this.router.navigate([AppRoutingRouteNames.AppUpdateInfo], navigationExtras);
            return true;
        }
        return false;
    }

    public getPatientInsuranceData() {
        const merged$ = combineLatest([
            this.commonBackendService.getPatientInsuranceCompanies(this.config.API_VERSION),
            this.commonBackendService.getPatientInsuranceTypes(this.config.API_VERSION),
        ]).pipe(
            map(([companies, types]) => {
                return {
                    companies,
                    types,
                };
            }),
        );
        return merged$;
    }

    public getSupportedCurrencies(): Observable<Array<CurrencyModel>> {
        return this.commonBackendService.getSupportedCurrencies(this.config.API_VERSION);
    }

    private async apiVersionInfo(): Promise<VersionModel> {
        const data = await firstValueFrom(this.versionBackendService.versionInfo(this.config.API_VERSION));
        this.$disabledFeatures.set(data.disabledFeatures);
        return data;
    }
}
